'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { MetaDefault } from '../../../../../components/typography';
import { Spinner } from '../../../../../components/spinner';
import { useTranslations } from 'next-intl';
import { TrackHistoryRequestInputSchema } from '@terra/shared-dto-chat-types';
import { HttpExecutionCard } from '../../../../artifacts/http/HttpExecutionCard';
import { useChatProxyHistory } from '../../../proxy-history/ChatProxyHistoryProvider';
import {
  buildHttpHitlRequestPayload,
  buildHttpHitlResponsePayload,
  type HttpHitlDisplayArgs,
} from './http-hitl-payload.utils';
import { getGuardrailReasonHeaderAction } from '../shared/guardrail-reason-header-action';

function hasHttpHitlDisplayArgs(
  input: Record<string, unknown>,
): input is HttpHitlDisplayArgs {
  return typeof input.url === 'string' && typeof input.method === 'string';
}

type TrackHistoryRequestHttpViewProps = {
  input: Record<string, unknown>;
  reviewMessage?: string;
  showReviewMessage?: boolean;
  runningText?: string;
  isRunning?: boolean;
  isTracked?: boolean;
  headerActions?: ReactNode;
};

export function TrackHistoryRequestHttpView({
  input,
  reviewMessage,
  showReviewMessage,
  runningText,
  isRunning,
  isTracked,
  headerActions,
}: TrackHistoryRequestHttpViewProps): ReactNode {
  const t = useTranslations('designSystem.chat.trackHistoryRequestHitlContent');
  const proxyHistory = useChatProxyHistory();
  const parsedInput = TrackHistoryRequestInputSchema.safeParse(input);
  const requestId = parsedInput.success
    ? parsedInput.data.requestId
    : undefined;
  const [displayArgs, setDisplayArgs] = useState<HttpHitlDisplayArgs | null>(
    hasHttpHitlDisplayArgs(input) ? input : null,
  );
  const [loadState, setLoadState] = useState<
    'idle' | 'loading' | 'error' | 'ready'
  >(hasHttpHitlDisplayArgs(input) ? 'ready' : requestId ? 'loading' : 'error');

  useEffect(() => {
    if (hasHttpHitlDisplayArgs(input)) {
      setDisplayArgs(input);
      setLoadState('ready');

      return;
    }

    if (!requestId) {
      setDisplayArgs(null);
      setLoadState('error');

      return;
    }

    if (!proxyHistory?.fetchHistoryRequestForHitlDisplay) {
      setDisplayArgs(null);
      setLoadState('error');

      return;
    }

    let cancelled = false;

    setLoadState('loading');

    void proxyHistory
      .fetchHistoryRequestForHitlDisplay(requestId)
      .then((loadedArgs) => {
        if (cancelled) {
          return;
        }

        if (!loadedArgs) {
          setDisplayArgs(null);
          setLoadState('error');

          return;
        }

        setDisplayArgs(loadedArgs);
        setLoadState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        console.warn(
          '[TrackHistoryRequestHttpView] Failed to load proxy history request',
          {
            requestId,
            error: error instanceof Error ? error : new Error(String(error)),
          },
        );
        setDisplayArgs(null);
        setLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [input, proxyHistory, requestId]);

  if (loadState === 'loading') {
    return (
      <VStack gap={2} width="100%" alignItems="center" py={4}>
        <Spinner size="sm" />
        <MetaDefault color="fg.muted">{t('loading')}</MetaDefault>
      </VStack>
    );
  }

  if (loadState === 'error' || !displayArgs) {
    return (
      <Box
        px={3}
        py={2}
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.subtle"
        bg="bg.subtle"
        width="100%"
      >
        <MetaDefault color="fg.muted">
          {requestId ? t('loadError', { requestId }) : t('invalidInput')}
        </MetaDefault>
      </Box>
    );
  }

  const requestPayload = buildHttpHitlRequestPayload(displayArgs);
  const responsePayload = showReviewMessage
    ? undefined
    : buildHttpHitlResponsePayload(displayArgs);
  const caption = isTracked
    ? t('trackedCaption')
    : isRunning
      ? runningText
      : undefined;

  if (displayArgs.wasProxyModified) {
    return (
      <VStack gap={3} width="100%">
        <Box
          px={3}
          py={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.error"
          bg="bg.canvas"
          width="100%"
        >
          <MetaDefault color="fg.error">
            {requestId
              ? t('proxyModifiedError', { requestId })
              : t('invalidInput')}
          </MetaDefault>
        </Box>
        <HttpExecutionCard
          requestPayload={requestPayload}
          responsePayload={responsePayload}
          headerActions={headerActions}
          afterReplayActions={getGuardrailReasonHeaderAction(input)}
          defaultRequestExpanded
          defaultResponseExpanded
        />
      </VStack>
    );
  }

  const resolvedReviewMessage =
    reviewMessage ?? (showReviewMessage ? t('reviewMessage') : undefined);

  return (
    <VStack gap={3} width="100%">
      {resolvedReviewMessage && (
        <Box
          px={3}
          py={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="bg.subtle"
          width="100%"
        >
          <MetaDefault color="fg.muted">{resolvedReviewMessage}</MetaDefault>
        </Box>
      )}
      <HttpExecutionCard
        requestPayload={requestPayload}
        responsePayload={responsePayload}
        headerActions={headerActions}
        afterReplayActions={getGuardrailReasonHeaderAction(input)}
        defaultRequestExpanded
        defaultResponseExpanded
      />
      {caption && (
        <MetaDefault color="fg.muted" px={1}>
          {caption}
        </MetaDefault>
      )}
    </VStack>
  );
}
